import { IPlayerSearchResponse } from "../../types/IPlayerSearchResponse";
import handleApiResponse from "../../utils/handleApiResponse";
import ApiResponse from "../../types/IApiResponse";
import { fetchData, postData } from "../../services/api";
import { filterWomensFootball } from "../../utils/womensFootballFilter";

import {
  ChemistryPlayerPositions,
  FormationData,
  IndividualPlayerStat,
  IOption,
  IPlayerData,
  ITeamSearch,
  OtherSquadMembers,
  PlayerCard,
  PlayerFromSamePositions,
  SubstitutePlayer,
  TeamChemistry,
  TeamPasses,
} from "../../types/IOption";
import apiUrls from "../../config/apiUrls";
import {
  IDisabledButtonsState,
  IEventTimeLine,
  IInjuryEvents,
  IMatchInfo,
  IMatchLevelEvents,
  ITeamMatchByMonths,
  ITimeLine,
  ITimeLineZoomLevel2,
} from "../../types/ITimeline";
import IRiskMapData, { IRiskMap } from "../../types/IRiskMapData";
import { HullData } from "../../types/IPitchProps";
import IApiResponse from "../../types/IApiResponse";
import { IPassingAttackData } from "../../utils/drawPassingAttack";
import { IMatchEventCardData } from "../../types/IMatchEventCardData";
import { IPlayerCardDetail } from "../../types/IPlayerCardDetail";
import { useLoading } from "../../contexts/LoadingContext";
import { usePitchWidgetContext } from "../../contexts/PitchWidgetContext";
import { formatNumberToKAndM } from "../../utils/formatNumberToKandM";

export interface ChemistryData {
  positionResults: ChemistryPlayerPositions[];
  teamChemstriys: TeamChemistry[];
  formationName: FormationData[];
  teamResultSummary: TeamResultSummary;
  avgSystemFitScore: number
}

export interface ISynergyData{
    player1: number,
    player2: number,
    score: number,
    isPredictive: boolean
}
export interface ICoaches{
  competitionId: number,
  competitionName: string,
  seasonId: number,
  seasonName: string,
  coachId: number,
  coachName: string,
  coachedMatches: number
}
export interface TeamResultSummary {
  leaguePosition: number;
  matchesWon: number;
  matchesLost: number;
  matchesDraw: number;
  totalGoals: number;
  totalGoalsAgainst: number;
  xGoals: number;
  xGoalAgainst: number;
  coaches: ICoaches[]
}

export interface MatchBasicDetails {
  matchId: number;
  label: string;
  dateTime: string;
  homeTeam: {
    teamId: number;
    teamName: string;
    teamUrl: string;
  };
  awayTeam: {
    teamId: number;
    teamName: string;
    teamUrl: string;
  };
}

interface PassingNetworkData {
  positionResults: ChemistryPlayerPositions[];
  teamPasses: TeamPasses[];
  advanceStatsByMatch: IndividualPlayerStat;
  matchBasicDetails?: MatchBasicDetails;
}

interface OnSwapPlayer {
  playerPlayedSameOnPositions: PlayerFromSamePositions[];
  teamSqurdPlayers: OtherSquadMembers[];
}

export const handleSearch = async (
  event: React.ChangeEvent<HTMLInputElement>,
  setSearchText: (text: string) => void,
  startLoading: () => void,
  setSearchData: ((data: IPlayerSearchResponse[]) => void) | undefined,
  stopLoading: () => void
) => {
  const value = event.target.value.trim();
  setSearchText(value);

  if (value.length < 3) {
    setSearchData?.([]);
    return;
  }
  startLoading();
  try {
    const response: IApiResponse<IPlayerSearchResponse[]> = await fetchData(`${apiUrls["PlayerSearch"]}?searchQuery=${value}`);
    handleApiResponse(
      response,
      (playerSearchData) => setSearchData?.(filterWomensFootball(playerSearchData)),
      (error) => {

        console.error("Error fetching Player Search Data:", error);
        setSearchData?.([]);
      }
    );
  } catch (error) {
    console.error("Error fetching Player Search Data:", error);
    setSearchData?.([]);
  } finally {
    stopLoading();
  }
};

export const fetchTeamData = async (searchTerm: string, setTeams: React.Dispatch<React.SetStateAction<ITeamSearch[]>>) => {
  try {
    const response: IApiResponse<ITeamSearch[]> = await fetchData(`${apiUrls["GetTeamsByFreeTextSearch"]}?searchString=${searchTerm}`);
    handleApiResponse(
      response,
      (teamsData) => {
        setTeams(filterWomensFootball(teamsData));
      },

      (error) => {
        console.error("Error fetching Home Team Data:", error);
        setTeams([]);
      }
    );
  } catch (error) {
    console.error("Error fetching Home Team Data:", error);
    setTeams([]);
  }
};

export const fetchOverallFormationData = async (
  selectedTeam: number,
  setFormationData: React.Dispatch<React.SetStateAction<FormationData[]>>,
  setSelectedFormation?: React.Dispatch<React.SetStateAction<number>>
) => {
  try {
    const response: IApiResponse<FormationData[]> = await fetchData(`${apiUrls["GetTeamFormationOfLastMatch"]}?teamId=${selectedTeam}`);
    handleApiResponse(
      response,
      (formationData) => {
        setFormationData(formationData);
        const maxUsage = Math.max(...formationData.map((f) => f.usagePercentage));
        const maxFormation = formationData.find((f) => f.usagePercentage === maxUsage);
        setSelectedFormation?.(maxFormation?.id || 0);
      },
      (error) => {
        console.error("Error fetching Home Team Data:", error);
        setFormationData([]);
      }
    );
  } catch (error) {
    console.error("Error fetching Home Team Data:", error);
    setFormationData([]);
  }
};

export const fetchChemistrySummaryData = async (
  isOverAll: boolean,
  selectedTeam: number,
  selectedSeason: number,
  matches: any[],
  setLeaguevalues: React.Dispatch<React.SetStateAction<TeamResultSummary>>,
  startLoading: () => void,
  stopLoading: () => void
) => {
  startLoading();
  try {
    const response: IApiResponse<TeamResultSummary> = await postData(`${apiUrls["GetTeamSummaryBar"]}`, {
      seasonId: selectedSeason,
      teamId: selectedTeam,
      isOverAll: isOverAll,
      matchIds: matches,
    });
    handleApiResponse(
      response,
      (teamResultSummary) => {
        if (setLeaguevalues) {
          setLeaguevalues?.(teamResultSummary);
        }
      },
      (error) => {
        console.error("Error fetching Chemistry Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Chemistry Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchChemistryData = async (
  selectedTeam: number,
  selectedSeason: number,
  setPositionResults: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>,
  setTeamChemistry: React.Dispatch<React.SetStateAction<TeamChemistry[]>>,
  startLoading: () => void,
  stopLoading: () => void,
  selectedFormation: number,
  setLastTeamChemistry: React.Dispatch<React.SetStateAction<TeamChemistry[]>>,
  setLastPositionResults: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>,
  setIsDisplayInitialRect: React.Dispatch<React.SetStateAction<boolean>>,
  isOverAll: boolean,
  matches: any[],
  setIsChemistryDataNotEnough: React.Dispatch<React.SetStateAction<boolean>>,
  setFormationData?: React.Dispatch<React.SetStateAction<FormationData[]>>,
  setSelectedFormation?: React.Dispatch<React.SetStateAction<number>>,
  setLeaguevalues?: React.Dispatch<React.SetStateAction<TeamResultSummary>>,
  setOverallSystemFitScore?: React.Dispatch<React.SetStateAction<number>> | undefined,
  showAnyWay?: boolean,
  setInitialPositionResults?:React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>
) => {
  // const {setInitialPositionResults} = usePitchWidgetContext();
  startLoading();
  try {
    const response: IApiResponse<ChemistryData> = await postData(`${apiUrls["TeamChemistryNetwork"]}`, {
      seasonId: selectedSeason,
      teamId: selectedTeam,
      formationId: selectedFormation,
      isOverAll: isOverAll,
      matchIds: matches,
      showAnyWay: showAnyWay
    });
    handleApiResponse(
      response,
      (chemistryData) => {
        setOverallSystemFitScore?.(chemistryData?.avgSystemFitScore);
        setIsChemistryDataNotEnough(false);
        setPositionResults(chemistryData?.positionResults);
        const adjustedChemistry = chemistryData?.teamChemstriys.map(connection => ({
          ...connection,
          score: connection.score === 100 ? 99 : connection.score
        }));
        setTeamChemistry(adjustedChemistry);
        setInitialPositionResults?.(response?.data?.positionResults);
        setLastTeamChemistry([]);
        setLastPositionResults([]);
        setIsDisplayInitialRect(false);
      },
      (error) => {
        setPositionResults([]);
        setTeamChemistry([]);
        if (setSelectedFormation && setFormationData) {
          setFormationData([]);
          setSelectedFormation(0)
        }
        console.error("Error fetching Chemistry Data:", error);
        if (error?.message === "No data for current season threshold not reached") {
          setIsChemistryDataNotEnough(true);
        }
      }
    );
  } catch (error) {
    setPositionResults([]);
    setTeamChemistry([]);
    if (setSelectedFormation && setFormationData) {
      setFormationData([]);
      setSelectedFormation(0)
    }
    console.error("Error fetching Chemistry Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchFormationData = async (
  selectedTeam: number,
  selectedSeason: number,
  startLoading: () => void,
  stopLoading: () => void,
  setFormationData: React.Dispatch<React.SetStateAction<FormationData[]>>,
  setSelectedFormation: React.Dispatch<React.SetStateAction<number>>,
  selectedMatch?: number[],
  isByMatch?: boolean,
  callback?: (formationData: FormationData[]) => void
): Promise<number | null> => {
  startLoading();
  try {
    const response: IApiResponse<FormationData[]> = await postData(`${apiUrls["TeamFormations"]}`, {
      teamId: selectedTeam,
      matchId: selectedMatch,
      //isByMatch: isByMatch,
      seasonId: selectedSeason,
    });
    let selectedFormationId: number | null = null;
    handleApiResponse(
      response,
      (formationData) => {
        if (formationData.length > 0) {
          selectedFormationId = formationData[0].id;
          setSelectedFormation(formationData[0].id);
          setFormationData(formationData);
        } else {
          setFormationData([]);
          callback?.([]);
        }
      },
      (error) => {
        console.error("Error fetching Formation Data:", error);
      }
    );
    return selectedFormationId === null ? 0 : selectedFormationId;
  } catch (error) {
    console.error("Error fetching Home Team Data:", error);
    return null;
  } finally {
    stopLoading();
  }
};

export const fetchSwapablePlayerData = async (
  selectedTeam: number,
  setPlayerFromSamePositions: React.Dispatch<React.SetStateAction<PlayerFromSamePositions[]>>,
  setOtherSquadMembers: React.Dispatch<React.SetStateAction<OtherSquadMembers[]>>,
  startLoading: () => void,
  stopLoading: () => void,
  playerId: number,
  positionCode: string,
  setSwapHoverPlayer: React.Dispatch<React.SetStateAction<number>>,
  seasonId: number
) => {
  startLoading();
  try {
    const response: IApiResponse<OnSwapPlayer> = await postData(`${apiUrls["OnSwapPlayerDetail"]}`, {
      teamId: selectedTeam,
      playerId: playerId,
      positionCode: positionCode,
      seasonId: seasonId,
    });
    handleApiResponse(
      response,
      (chemistryData) => {
        setPlayerFromSamePositions(chemistryData.playerPlayedSameOnPositions);
        setOtherSquadMembers(chemistryData.teamSqurdPlayers);
        setSwapHoverPlayer(playerId);
      },
      (error) => {
        console.error("Error fetching Swapable Player Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Swapable Player Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchSubstituteOptions = async (
  selectedTeam: number,
  setSubstitutePlayerOptions: React.Dispatch<React.SetStateAction<SubstitutePlayer[]>>,
  startLoading: () => void,
  stopLoading: () => void,
  playerId: number,
  positionCode: string,
  matchId: number,
  setSwapHoverPlayer: React.Dispatch<React.SetStateAction<number>>
) => {
  startLoading();
  try {
    const response: IApiResponse<SubstitutePlayer[]> = await postData(`${apiUrls["GetSubstitutionOnSwap"]}`, {
      teamId: selectedTeam,
      playerId: playerId,
      matchId: matchId,
      positionCode: positionCode,
    });
    handleApiResponse(
      response,
      (chemistryData) => {
        setSubstitutePlayerOptions(chemistryData);
        setSwapHoverPlayer(playerId);
      },
      (error) => {
        console.error("Error fetching Substitute Options:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Substitute Options:", error);
  } finally {
    stopLoading();
  }
};

export const fetchAfterSwapData = async (
  startLoading: () => void,
  stopLoading: () => void,
  playerToSwap: number,
  playerToSwapWith: number,
  swappingPositionCode: string,
  formationId: number,
  positionResult: ChemistryPlayerPositions[],
  teamChemistry: TeamChemistry[],
  setPositionResults: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>,
  setTeamChemistry: React.Dispatch<React.SetStateAction<TeamChemistry[]>>,
  setSwappedPositionResults: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>,
  setSwappedTeamChemistry: React.Dispatch<React.SetStateAction<TeamChemistry[]>>,
  teamId: number,
  isOverall: boolean,
  seasonId: number,
  matchIds: number[],
  setOverallSystemFitScore?: React.Dispatch<React.SetStateAction<number>> | undefined
): Promise<ChemistryPlayerPositions[] | undefined> => {
  startLoading();
  try {
    const response: IApiResponse<ChemistryData> = await postData(`${apiUrls["SwapPlayer"]}`, {
      playerToSwap,
      playerToSwapWith,
      swappingPositionCode,
      formationId,
      positionResult,
      teamId: teamId,
      isOverall: isOverall,
      seasonId: seasonId,
      matchIds: matchIds,
    });
    let positionResults;
    handleApiResponse(
      response,
      (chemistryData) => {
        setOverallSystemFitScore?.(chemistryData?.avgSystemFitScore);
        setPositionResults(chemistryData.positionResults);
        setTeamChemistry(chemistryData.teamChemstriys);
        setSwappedPositionResults(positionResult);
        setSwappedTeamChemistry(teamChemistry);
        positionResults = chemistryData?.positionResults;
      },
      (error) => {
        console.error("Error fetching After Swap Data:", error);
      }
    );
    return positionResults;
  } catch (error) {
    console.error("Error fetching After Swap Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchAfterSubstitudeData = async (
  startLoading: () => void,
  stopLoading: () => void,
  teamId: number,
  matchId: number,
  playerToSwap: number,
  playerToSwapWith: number,
  swappingPositionCode: string,
  formationId: number,
  positionResult: ChemistryPlayerPositions[],
  setPassNetworkPlayers: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>,
  setPassNetworkPlayerPasses: React.Dispatch<React.SetStateAction<TeamPasses[]>>,
  isActualPositionToggled: boolean
) => {
  startLoading();
  try {
    const response: IApiResponse<PassingNetworkData> = await postData(`${apiUrls["SwapPlayerForPN"]}`, {
      teamId: teamId,
      matchId: matchId,
      playerToSwap: playerToSwap,
      playerToSwapWith: playerToSwapWith,
      swappingPositionCode: swappingPositionCode,
      formationId: isActualPositionToggled ? 0 : formationId,
      positionResult: positionResult,
      isByActualPosition: isActualPositionToggled,
    });
    handleApiResponse(
      response,
      (chemistryData) => {
        setPassNetworkPlayers(chemistryData.positionResults);
        setPassNetworkPlayerPasses(chemistryData.teamPasses);
      },
      (error) => {
        console.error("Error fetching After Substitute Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching After Substitute Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchIndividualChemistry = async (
  startLoading: () => void,
  stopLoading: () => void,
  onClickPlayerId: number,
  positionCode: string,
  setPositionResults: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>,
  setTeamChemistry: React.Dispatch<React.SetStateAction<TeamChemistry[]>>,
  positionResult: ChemistryPlayerPositions[],
  formationId: number,
  isOverall: boolean,
  teamId: number,
  seasonId: number,
  matchIds?: number[]
) => {
  startLoading();
  try {
    const response: IApiResponse<ChemistryData> = await postData(`${apiUrls["OnPlayerClick"]}`, {
      positionCode,
      onClickPlayerId,
      positionResult,
      formationId,
      isOverAll: isOverall,
      teamId: teamId,
      seasonId: seasonId,
      matchIds: matchIds,
    });
    handleApiResponse(
      response,
      (chemistryData) => {
        setPositionResults(chemistryData.positionResults);
        setTeamChemistry(chemistryData.teamChemstriys);
      },
      (error) => {
        console.error("Error fetching Individual Chemistry Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Individual Chemistry Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchIndividualPassing = async (
  startLoading: () => void,
  stopLoading: () => void,
  teamId: number,
  matchId: number,
  onClickPlayerId: number,
  setPassNetworkPlayers: React.Dispatch<React.SetStateAction<ChemistryPlayerPositions[]>>,
  setPassNetworkPlayerPasses: React.Dispatch<React.SetStateAction<TeamPasses[]>>,
  setIndividualStat: React.Dispatch<React.SetStateAction<IndividualPlayerStat | undefined>>,
  positionResult: ChemistryPlayerPositions[],
  formationId: number,
  isActualPositionToggled: boolean
) => {
  startLoading();
  try {
    const response: IApiResponse<PassingNetworkData> = await postData(`${apiUrls["OnPlayerClickForPN"]}`, {
      teamId: teamId,
      matchId: matchId,
      formationId: isActualPositionToggled ? 0 : formationId,
      onClickPlayerId: onClickPlayerId,
      positionResult: positionResult,
      isByActualPosition: isActualPositionToggled,
    });
    handleApiResponse(
      response,
      (chemistryData) => {
        setIndividualStat(chemistryData.advanceStatsByMatch);
        setPassNetworkPlayers(chemistryData.positionResults);
        setPassNetworkPlayerPasses(chemistryData.teamPasses);
      },
      (error) => {
        console.error("Error fetching Individual Passing Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Individual Passing Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchMatchTimeline = async (
  startLoading: () => void,
  stopLoading: () => void,
  teamID: number,
  setDisplayedMatches: React.Dispatch<React.SetStateAction<any[]>>,
  setDisabledButtons: React.Dispatch<React.SetStateAction<IDisabledButtonsState>>,
  setSelectedSeasonId: React.Dispatch<React.SetStateAction<number>>,
  selectedSeasonId: number,
  isPrevious: boolean,
  isNext: boolean,
  zoomLevelId: number,
  setTeamMatchesByMonth: React.Dispatch<React.SetStateAction<ITeamMatchByMonths[]>>,
  setTimeLineTag: React.Dispatch<React.SetStateAction<string>>,
  setSeasonName: React.Dispatch<React.SetStateAction<string>>,
  isDisplayMonthMatches: boolean,
  callback?: (updatedMatches: any[], updatedSeasonId: number) => Promise<void>
) => {
  startLoading();
  try {
    const response: IApiResponse<ITimeLine> = await postData(`${apiUrls["GetTimeLine"]}`, {
      zoomLevelId: zoomLevelId,
      teamId: teamID,
      seasonId: selectedSeasonId,
      forCurrentSeason: selectedSeasonId === 0,
      isPrevious: isPrevious,
      isNext: isNext,
    });
    handleApiResponse(
      response,
      async (timelineData) => {
        const { matches, canPerformPrevious, canPerformNext, selectedSeasonId, teamMatchByMonths, timeLineTag, seasonName } = timelineData;
        if (!isDisplayMonthMatches) {
          const withShortnedDate = matches.map((item) => {
            return {
              ...item,
              matchLabel: item.matchLabel.split("(")[0],
            };
          });
          // setDisplayedMatches(withShortnedDate);
          setDisabledButtons((prevState) => ({
            ...prevState,
            play: false,
            previous: !canPerformPrevious,
            next: !canPerformNext,
            zoomIn: zoomLevelId !== 3,
            zoomOut: zoomLevelId !== 3,
          }));
          setSelectedSeasonId(selectedSeasonId);
          setTeamMatchesByMonth(teamMatchByMonths);
          setTimeLineTag(timeLineTag);
          setSeasonName(seasonName);
          await callback?.(withShortnedDate, selectedSeasonId);
          setDisplayedMatches(withShortnedDate);
        } else {
          setSelectedSeasonId(selectedSeasonId);
          setTeamMatchesByMonth(timelineData.teamMatchByMonths);
        }
      },
      (error) => {
        console.error("Error fetching match timeline:", error);
      }
    );
  } catch (error) {
    console.error("Unexpected error fetching match timeline:", error);
  } finally {
    if (!callback) {
      stopLoading();
    }
  }
};

export const fetchMatchTimelineZoomLevel2 = async (
  startLoading: () => void,
  teamID: number,
  setDisplayedMatches: React.Dispatch<React.SetStateAction<any[]>>,
  setDisabledButtons: React.Dispatch<React.SetStateAction<IDisabledButtonsState>>,
  setSelectedSeasonId: React.Dispatch<React.SetStateAction<number>>,
  selectedSeasonId: number,
  thirdOfSeason: string,
  isPrevious: boolean,
  isNext: boolean,
  zoomLevelId: number,
  setTimeLineTag: React.Dispatch<React.SetStateAction<string>>,
  setSeasonName: React.Dispatch<React.SetStateAction<string>>,
  callback: (updatedMatches: any[], seasonId: number) => Promise<void>,
  setDrawCount?: React.Dispatch<React.SetStateAction<number>>,
  setLossCount?: React.Dispatch<React.SetStateAction<number>>,
  setWinCount?: React.Dispatch<React.SetStateAction<number>>
) => {
  startLoading();
  try {
    const part = thirdOfSeason === "FinalThird" ? 3 : thirdOfSeason === "MiddleThird" ? 2 : 1;
    const response: IApiResponse<ITimeLineZoomLevel2> = await postData(`${apiUrls["GetTimeLine"]}`, {
      zoomLevelId: zoomLevelId,
      teamId: teamID,
      seasonId: selectedSeasonId,
      part: part,
      forCurrentSeason: selectedSeasonId === 0,
      isPrevious: isPrevious,
      isNext: isNext,
    });
    handleApiResponse(
      response,
      async (timelineData) => {
        const matches = timelineData.matches;
        const withShortnedDate =
          matches !== null
            ? matches.map((item) => {
                return {
                  ...item,
                  matchLabel: item.matchLabel.split("(")[0],
                };
              })
            : [];
        // setDisplayedMatches(withShortnedDate);
        setDisabledButtons((prevState) => ({
          ...prevState,
          play: false,
          previous: part === 1,
          next: part === 3,
          zoomIn: false,
          zoomOut: false,
        }));
        setSelectedSeasonId(timelineData.seasonId);
        setTimeLineTag(timelineData.timeLineTag);
        setSeasonName(timelineData.seasonName);
        await callback(withShortnedDate, timelineData.seasonId);
        setDisplayedMatches(withShortnedDate);
        if (setLossCount && setDrawCount && setWinCount) {
          setDrawCount!(timelineData.drawCount);
          setWinCount!(timelineData.winCount);
          setLossCount!(timelineData.lossCount);
        }
      },
      (error) => {
        console.error("Error fetching Match Timeline Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Match Timeline Data:", error);
  } finally {
    // stopLoading();
  }
};

export const fetchPlayerCardData = async (
  startLoading: () => void,
  stopLoading: () => void,
  playerID: number,
  setPlayerCardDeatils: React.Dispatch<React.SetStateAction<IPlayerCardDetail | undefined>>
) => {
  startLoading();
  try {
    const response: IApiResponse<IPlayerCardDetail> = await postData(`${apiUrls["GetPlayerCardDetail"]}`, {
      // userId: 0,
      playerId: playerID,
    });
    handleApiResponse(
      response,
      (playerData) => {
        setPlayerCardDeatils({
          playerId: playerData.playerId,
          givenName: playerData.givenName,
          familyname: playerData.familyname,
          playerImageURL: playerData.playerImageURL,
          isInjured: playerData.isInjured,
          onLoan: playerData.onLoan,
          primaryPlayingPosition: playerData.primaryPlayingPosition,
          age: playerData.age,
          heightInCM: playerData?.heightInCM ?? playerData?.heightInCm,
          playerAttributesByPosition: playerData.playerAttributesByPosition,
          contributionValue: playerData.contributionValue,
          performanceValue: playerData.performanceValue,
          clubImageURL: playerData?.clubImageURL,
          isPlayerInShortlist: playerData.isPlayerInShortlist,
          playerLabel: playerData.playerLabel,
          isEasternPlayer: playerData.isEasternPlayer,
          ...(playerData?.archetypes?.length > 0 && {
            archetypeName: playerData.archetypes[0].archetypeName,
            archetypeId: playerData.archetypes[0].id
          }),
          trueValueText: playerData?.trueValueText ? playerData.trueValueText : (playerData?.trueValue ? formatNumberToKAndM(playerData.trueValue) : undefined)
        });
      },
      (error) => {
        console.error("Error fetching Player Card Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Player Card Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchPlayerPassingNetworks = async (
  teamId: number,
  matchId: number,
  selectedFormation: number,
  startLoading: () => void,
  stopLoading: () => void,
  isActualPositionToggled: boolean,
  setPassNetworkPlayers: React.Dispatch<React.SetStateAction<any[]>>,
  setPassNetworkPlayerPasses: React.Dispatch<React.SetStateAction<any[]>>,
  setLastPassNetworkPlayers: React.Dispatch<React.SetStateAction<any[]>>,
  setLastPassNetworkPlayerPasses: React.Dispatch<React.SetStateAction<any[]>>,
  setMatchBasicDetails?: React.Dispatch<React.SetStateAction<MatchBasicDetails | null>>
) => {
  if (teamId <= 0) {
    setPassNetworkPlayers([]);
    setPassNetworkPlayerPasses([]);
    setMatchBasicDetails?.(null);
    return;
  }
  startLoading();
  try {
    const response: IApiResponse<PassingNetworkData> = await postData(apiUrls.PlayerPassingNetwork, {
      teamId: teamId,
      matchId: matchId,
      formationId: isActualPositionToggled ? 0 : selectedFormation,
      isByActualPosition: isActualPositionToggled,
    });
    handleApiResponse(
      response,
      (passingNetwork) => {
        setPassNetworkPlayers(passingNetwork.positionResults);
        setPassNetworkPlayerPasses(passingNetwork.teamPasses);
        setMatchBasicDetails?.(passingNetwork.matchBasicDetails || null);
        setLastPassNetworkPlayers([]);
        setLastPassNetworkPlayerPasses([]);
      },
      (error) => {
        console.error("Error fetching passing network data:", error);
        setMatchBasicDetails?.(null);
      }
    );
  } catch (error) {
    console.error("Error fetching passing network data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchSeasonData = async (
  startLoading: () => void,
  stopLoading: () => void,
  teamID: number,
  setSeasonData: React.Dispatch<React.SetStateAction<any[]>>
) => {
  startLoading();
  try {
    const response: IApiResponse<any[]> = await fetchData(`${apiUrls.GetCompetitionSeasonByTeamId}?TeamId=${teamID}`);
    handleApiResponse(
      response,
      (seasonData) => {
        setSeasonData(seasonData);
      },
      (error) => {
        console.error("Error fetching Season Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching season data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchEventTimeline = async (
  startLoading: () => void,
  stopLoading: () => void,
  teamID: number,
  matchID: number,
  setInjuryEvents: React.Dispatch<React.SetStateAction<IInjuryEvents | undefined>>,
  setMatchEndTime: React.Dispatch<React.SetStateAction<number>>,
  setMatchLevelEvents: React.Dispatch<React.SetStateAction<IMatchLevelEvents[]>>,
  setMatchInfo: React.Dispatch<React.SetStateAction<IMatchInfo | undefined>>,
  setSelectedMatch: React.Dispatch<React.SetStateAction<number>>,
  setTimeLineTag: React.Dispatch<React.SetStateAction<string>>,
  setDisabledButtons: React.Dispatch<React.SetStateAction<IDisabledButtonsState>>,
  isPrevious: boolean,
  isNext: boolean,
  setMatchDate: React.Dispatch<React.SetStateAction<string>>,
  callback?: (matchId: number) => void
): Promise<number | undefined> => {
  startLoading();
  try {
    const response: IApiResponse<IEventTimeLine> = await postData(`${apiUrls["GetMatchLevelTimeLineData"]}`, {
      teamId: teamID,
      matchId: matchID,
      isNext: isNext,
      isPrevious: isPrevious,
      isLastMatch: matchID === 0,
    });
    handleApiResponse(
      response,
      (eventTimelineData) => {
        setMatchEndTime(eventTimelineData.endMatchTimestampSeconds);
        setInjuryEvents(eventTimelineData.injuryEvents);
        setMatchLevelEvents(eventTimelineData.matchLevelEvents);
        setMatchInfo(eventTimelineData.matchInfo);
        setSelectedMatch(eventTimelineData.selectedMatchId);
        setTimeLineTag(eventTimelineData.timeLineTag);
        setDisabledButtons((prevState) => ({
          ...prevState,
          play: false,
          previous: !eventTimelineData.canPerformPrevious,
          next: !eventTimelineData.canPerformNext,
          zoomIn: true,
          zoomOut: false,
        }));
        setMatchDate(eventTimelineData.matchDate);
        callback?.(eventTimelineData.selectedMatchId);
        return eventTimelineData.selectedMatchId;
      },
      (error) => {
        console.error("Error fetching  event timeline Data:", error);
        return undefined;
      }
    );
  } catch (error) {
    console.error("Error fetching event timeline data:", error);
    return undefined;
  } finally {
    stopLoading();
  }
};

export const fetchRiskMapData = async (
  startLoading: () => void,
  stopLoading: () => void,
  seasonId: number,
  teamId: number,
  matchIds: number[],
  setRiskMapData: React.Dispatch<React.SetStateAction<IRiskMap[]>>
) => {
  startLoading();
  try {
    const response: IApiResponse<IRiskMap[]> = await postData(`${apiUrls["ThreatMap"]}`, {
      seasonId: seasonId,
      teamId: teamId,
      matchIds: matchIds,
    });
    handleApiResponse(
      response,
      (riskMapData) => {
        setRiskMapData(riskMapData);
      },
      (error) => {
        console.error("Error fetching Risk Map Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Risk Map Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchHullData = async (
  selectedTeam: number,
  selectedSeason: number,
  selectedFormation: number,
  selectedMatches: any[] | null,
  setHullData: React.Dispatch<React.SetStateAction<HullData>>,
  startLoading: () => void,
  stopLoading: () => void
) => {
  startLoading();
  try {
    const response: IApiResponse<HullData> = await postData(`${apiUrls["DefensiveHull"]}`, {
      seasonId: selectedSeason,
      teamId: selectedTeam,
      matchIds: selectedMatches,
      formationId: selectedFormation,
    });
    handleApiResponse(
      response,
      (hullData) => {
        setHullData(hullData);
      },
      (error) => {
        console.error("Error fetching Hull Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Hull Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchPlayersData = async (
  startLoading: () => void,
  stopLoading: () => void,
  teamID: number,
  matchID: number,
  setPlayerData: React.Dispatch<React.SetStateAction<IPlayerData[]>>,
  callback?: (playerData: IPlayerData[]) => void
) => {
  startLoading();
  try {
    const response: IApiResponse<IPlayerData[]> = await postData(`${apiUrls["GetPlayersByTeamMatch"]}`, {
      teamId: teamID,
      matchId: matchID,
    });
    handleApiResponse(
      response,
      (playerData) => {
        setPlayerData(playerData);
        callback?.(playerData);
      },
      (error) => {
        console.error("Error fetching Player Data By Team Match:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Player Data By Team Match:", error);
  } finally {
    stopLoading();
  }
};

export const fetchPlayerAttackPassData = async (
  startLoading: () => void,
  stopLoading: () => void,
  playerID: number,
  matchID: number,
  setPassingAttackData: React.Dispatch<React.SetStateAction<IPassingAttackData[]>>
) => {
  startLoading();
  try {
    const response: IApiResponse<IPassingAttackData[]> = await postData(`${apiUrls["GetPlayerAttackPassData"]}`, {
      playerId: playerID,
      matchId: matchID,
    });
    handleApiResponse(
      response,
      (playerData) => {
        setPassingAttackData(playerData);
      },
      (error) => {
        console.error("Error fetching Passing Attack Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Passing Attack Data:", error);
  } finally {
    stopLoading();
  }
};

export const fetchMatchInformationData = async (
  matchId: number,
  startLoading: () => void,
  stopLoading: () => void,
  setMatchEventInfo: React.Dispatch<React.SetStateAction<IMatchEventCardData | null | undefined>>,
  setMatchDate: React.Dispatch<React.SetStateAction<string>>,
  callback?: (matchEventData: IMatchEventCardData) => void
) => {
  startLoading();
  try {
    const response: IApiResponse<IMatchEventCardData> = await postData(`${apiUrls["GetMatchEventsCardData"]}`, {
      matchId: matchId,
    });

    handleApiResponse(
      response,
      (matchEventInfo) => {
        setMatchEventInfo(matchEventInfo);
        callback?.(matchEventInfo);
        // if (formationData.length > 0) {
        //   selectedFormationId = formationData[0].id;
        //   setSelectedFormation(formationData[0].id);
        //   setFormationData(formationData);
        // }
        // else {
        //   setFormationData([]);
        //   callback?.([]);
        // }
      },
      (error) => {
        console.error("Error fetching match information Data:", error);
      }
    );
    // return selectedFormationId;
  } catch (error) {
    console.error("Error fetching match information Data:", error);
    return null;
  } finally {
    stopLoading();
  }
};

export const fetchPredictiveChemistryConnection = async (
  player1: number,
  player2: number,
  startLoading: () => void,
  stopLoading: () => void,
  setTeamChemistry: React.Dispatch<React.SetStateAction<TeamChemistry[]>>,
  teamChemistry: TeamChemistry[]
) => {
  startLoading();
  try {
    const response: IApiResponse<number> = await postData(`${apiUrls["GetChemistrybetweenPlayers"]}`, {
      player1: player1,
      player2: player2,
    });
    handleApiResponse(
      response,
      (chemistryData) => {
        const newLinkObject = teamChemistry.map((link) => {
          if (link.player1 === player1 && link.player2 === player2) {
            link.isPredictive = true;
            link.score = chemistryData;
            return link;
          } else {
            return link;
          }
        });
        setTeamChemistry(newLinkObject);
      },
      (error) => {
        console.error("Error fetching Chemistry Data:", error);
      }
    );
  } catch (error) {
    console.error("Error fetching Chemistry Data:", error);
  } finally {
    stopLoading();
  }
};
